/**
 * Knowledge Base Create/Edit Modal
 * 
 * Modal dialog for creating new knowledge bases or editing existing ones.
 * Handles form validation and API mutations.
 */

import { useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { useI18n } from '@/i18n';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { getErrorCode, getErrorMessage } from '@/lib/api-client';
import {
  useCreateKnowledgeBase,
  useUpdateKnowledgeBase,
  type KnowledgeBase,
  type KnowledgeBaseScopeType,
} from '@/hooks/use-knowledge-bases';

/**
 * Scope type options with descriptions
 */
const SCOPE_OPTIONS: Array<{ value: KnowledgeBaseScopeType; label: string; hint: string }> = [
  { 
    value: 'shared', 
    label: 'Shared', 
    hint: 'Accessible to all tenant members' 
  },
  { 
    value: 'client', 
    label: 'Client', 
    hint: 'Specific to certain client groups' 
  },
  { 
    value: 'internal', 
    label: 'Internal', 
    hint: 'Internal team use only' 
  },
  { 
    value: 'restricted', 
    label: 'Restricted', 
    hint: 'Limited to specific authorized users' 
  },
];

/**
 * Form field values
 */
interface FormValues {
  name: string;
  description: string;
  scope_type: KnowledgeBaseScopeType;
}

/**
 * Modal component props
 */
interface KnowledgeBaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  knowledgeBase?: KnowledgeBase;
}

export function KnowledgeBaseModal({ 
  open, 
  onOpenChange, 
  knowledgeBase 
}: KnowledgeBaseModalProps) {
  const { t } = useI18n();
  const isEditMode = Boolean(knowledgeBase);

  // Form setup with react-hook-form
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      description: '',
      scope_type: 'shared',
    },
  });
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // Mutations
  const createMutation = useCreateKnowledgeBase();
  const updateMutation = useUpdateKnowledgeBase();

  // Reset form when modal opens or knowledge base changes
  useEffect(() => {
    if (open) {
      if (knowledgeBase) {
        reset({
          name: knowledgeBase.name,
          description: knowledgeBase.description ?? '',
          scope_type: knowledgeBase.scope_type,
        });
      } else {
        reset({
          name: '',
          description: '',
          scope_type: 'shared',
        });
      }
    }
  }, [open, knowledgeBase, reset]);

  // Handle form submission
  const onSubmit = useCallback(async (formData: FormValues) => {
    try {
      if (isEditMode && knowledgeBase) {
        await updateMutation.mutateAsync({
          id: knowledgeBase.id,
          name: formData.name,
          description: formData.description || undefined,
          scope_type: formData.scope_type,
        });
        toast.success(t('admin.knowledge.knowledgeBases.modal.updateSuccess'));
      } else {
        await createMutation.mutateAsync({
          name: formData.name,
          description: formData.description || undefined,
          scope_type: formData.scope_type,
        });
        toast.success(t('admin.knowledge.knowledgeBases.modal.createSuccess'));
      }
      onOpenChange(false);
    } catch (err) {
      const code = getErrorCode(err);
      if (code === 'KNOWLEDGE_BASE_NAME_EXISTS') {
        setError('name', { message: t('admin.knowledge.knowledgeBases.modal.nameExists') });
      } else {
        const errorMessage = isEditMode 
          ? t('admin.knowledge.knowledgeBases.modal.updateError')
          : t('admin.knowledge.knowledgeBases.modal.createError');
        toast.error(`${errorMessage}: ${getErrorMessage(err)}`);
      }
    }
  }, [isEditMode, knowledgeBase, createMutation, updateMutation, onOpenChange, t]);

  // Handle scope type selection
  const handleScopeChange = useCallback((value: string) => {
    setValue('scope_type', value as KnowledgeBaseScopeType);
  }, [setValue]);

  const currentScopeType = watch('scope_type');
  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { if (isDirty) { setConfirmDiscardOpen(true); } else { onOpenChange(false); } } }}>
      <DialogContent
        className="sm:max-w-[480px]"
        dirty={isDirty}
        discardTitle={t('common.discardChanges.title') ?? 'Discard changes?'}
        discardMessage={t('common.discardChanges.message') ?? 'You have unsaved changes. If you close now, they will be lost.'}
        discardConfirmLabel={t('common.discardChanges.discard') ?? 'Discard'}
        discardCancelLabel={t('common.discardChanges.keepEditing') ?? 'Keep editing'}
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode 
              ? t('admin.knowledge.knowledgeBases.modal.editTitle')
              : t('admin.knowledge.knowledgeBases.modal.createTitle')
            }
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 mt-4">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="kb-name">
              {t('admin.knowledge.knowledgeBases.modal.nameLabel')} *
            </Label>
            <Input
              id="kb-name"
              placeholder={t('admin.knowledge.knowledgeBases.modal.namePlaceholder')}
              {...register('name', { 
                required: t('admin.knowledge.knowledgeBases.modal.nameRequired'),
                minLength: {
                  value: 2,
                  message: t('admin.knowledge.knowledgeBases.modal.nameTooShort'),
                },
                maxLength: {
                  value: 100,
                  message: t('admin.knowledge.knowledgeBases.modal.nameTooLong'),
                },
              })}
              aria-invalid={errors.name ? 'true' : 'false'}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Description Field */}
          <div className="space-y-2">
            <Label htmlFor="kb-description">
              {t('admin.knowledge.knowledgeBases.modal.descriptionLabel')}
            </Label>
            <Textarea
              id="kb-description"
              placeholder={t('admin.knowledge.knowledgeBases.modal.descriptionPlaceholder')}
              rows={3}
              {...register('description', {
                maxLength: {
                  value: 500,
                  message: t('admin.knowledge.knowledgeBases.modal.descriptionTooLong'),
                },
              })}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>

          {/* Scope Type Field */}
          <div className="space-y-2">
            <Label htmlFor="kb-scope">
              {t('admin.knowledge.knowledgeBases.modal.scopeLabel')}
            </Label>
            <Select value={currentScopeType} onValueChange={handleScopeChange}>
              <SelectTrigger id="kb-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {option.hint}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => { if (isDirty) { setConfirmDiscardOpen(true); } else { onOpenChange(false); } }}
              disabled={isMutating}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={isMutating || isSubmitting}
            >
              {isMutating 
                ? (isEditMode 
                    ? t('admin.knowledge.knowledgeBases.modal.saving') 
                    : t('admin.knowledge.knowledgeBases.modal.creating'))
                : (isEditMode 
                    ? t('admin.knowledge.knowledgeBases.modal.save') 
                    : t('admin.knowledge.knowledgeBases.modal.create'))
              }
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={t('common.discardChanges.title') ?? 'Discard changes?'}
        message={t('common.discardChanges.message') ?? 'You have unsaved changes. If you close now, they will be lost.'}
        confirmLabel={t('common.discardChanges.discard') ?? 'Discard'}
        cancelLabel={t('common.discardChanges.keepEditing') ?? 'Keep editing'}
      />
    </Dialog>
  );
}
